/* eslint-disable no-nested-ternary */
import { Print } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { uniq } from 'lodash';
import { useMemo, useState } from 'react';
import { DishUnit } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import FilterDropdown from '../../components/FilterDropdown';
import Loader from '../../components/Loader';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import currency from '../../utils/currency';
import { getOrderItemTotalPrice } from '../../utils/getBillCalc';
import { isNotNull } from '../../utils/isNotNull';
import { getByCard } from '../../utils/paid';
import ReportBySalesPrintModal from './ReportBySalesPrintModal';
import { useGetArchivedOrders } from './useGetArchivedOrders';

export type ReportItem = {
  id: string;
  dishName: string;
  qnt: number;
  weight: number;
  unit: DishUnit;
  byCard: number;
  cash: number;
  total: number;
  promoSum: number;
  discount: number;
  service: number;
  group?: string;
  section?: string;
  cost: number;
};

type Row = ReportItem;
type Props = {
  timeRange: [number, number];
};

const ReportBySales = ({ timeRange }: Props) => {
  const { lang } = useLocale();
  const { cafeSlug, cafe } = useAdmin();
  const {
    dishesMap,
    categoriesMap,
    sectionsMap,
    legalEntities,
    legalEntitiesMap,
    selfOrderPoints,
    selfOrderPointsMap,
  } = useCafe();
  const [print, setPrint] = useState(false);
  const [section, setSection] = useState('');
  const [workerName, setWorkerName] = useState('');
  const [tableName, setTableName] = useState('');
  const [orderType, setOrderType] = useState('');
  const [legalEntityId, setLegalEntityId] = useState('');
  const [selfOrderPointId, setSelfOrderPointId] = useState('');

  const { archivedOrders, isLoading } = useGetArchivedOrders(timeRange);

  const orders = useMemo(() => archivedOrders?.filter((o) => !!o.paid) || [], [archivedOrders]);

  const sections = useMemo(
    () =>
      uniq(
        orders
          .map((o) =>
            Object.values(o.items).map(
              (i) => sectionsMap[categoriesMap[dishesMap[i.dishId]?.categoryId || '']?.sectionId || '']?.name
            )
          )
          .flat()
      ).filter(isNotNull),
    [categoriesMap, dishesMap, orders, sectionsMap]
  );

  const workerNames = useMemo(() => uniq(orders.map((o) => o.workerName)).filter((o) => !!o), [orders]);
  const tableNames = useMemo(() => uniq(orders.map((o) => o.tableName)).filter((o) => !!o), [orders]);
  const legalEntitiesId = useMemo(() => legalEntities.map((i) => i.id), [legalEntities]);
  const selfOrderPointIds = useMemo(() => selfOrderPoints.map((i) => i.id), [selfOrderPoints]);
  const orderTypes = useMemo(
    () => uniq(orders.map((o) => (o.type ? t[`order_type_${o.type}`][lang] : t.order_type_regular[lang]))),
    [lang, orders]
  );

  const filteredOrders = useMemo(() => {
    const filteredByWorker = workerName ? orders.filter((o) => o.workerName === workerName) : orders;
    const filteredByTable = tableName ? filteredByWorker.filter((o) => o.tableName === tableName) : filteredByWorker;
    const filteredBySelfOrderPoint = selfOrderPointId
      ? filteredByTable.filter((o) => o.selfOrderPointId === selfOrderPointId)
      : filteredByTable;

    const filteredByType = orderType
      ? filteredBySelfOrderPoint.filter((o) => {
          if (orderType === t.order_type_regular[lang] && (!o.type || o.type === 'regular')) {
            return true;
          }
          if (orderType === t.order_type_delivery[lang] && o.type === 'delivery') {
            return true;
          }
          if (orderType === t.order_type_selfOrder[lang] && o.type === 'selfOrder') {
            return true;
          }
          if (orderType === t.order_type_tableOrder[lang] && o.type === 'tableOrder') {
            return true;
          }
          return false;
        })
      : filteredBySelfOrderPoint;

    const filteredByLegalEntity = legalEntityId
      ? filteredByType.map((order) => ({
          ...order,
          items: Object.fromEntries(
            Object.entries(order.items).filter(
              ([_, item]) =>
                item.legalEntityId === legalEntityId || (!item.legalEntityId && legalEntityId === legalEntities[0]?.id)
            )
          ),
        }))
      : filteredByType;

    return filteredByLegalEntity;
  }, [lang, legalEntities, legalEntityId, orderType, orders, selfOrderPointId, tableName, workerName]);

  const rows = useMemo(() => {
    const rowMap: Record<string, ReportItem> = {};
    filteredOrders.forEach((order) => {
      Object.values(order.items).forEach((item) => {
        const prev = rowMap[item.dishId];

        const total = getOrderItemTotalPrice(item);
        const byCard = (total * getByCard(order.paid)) / (order.paid?.total || 0);
        const cash = total - byCard;
        const promoApplied = order.paid?.promotionsApplied?.find((p) => p.appliedForOrderItemIds.includes(item.id));
        const promoSum = promoApplied
          ? promoApplied.discountType === 'percentage'
            ? (total * Number(promoApplied.discountValue)) / 100
            : promoApplied.discountSum / promoApplied.appliedForOrderItemIds.length
          : 0;
        const discount = Number(order.paid?.discount ? (order.paid.discount * (total - promoSum)) / 100 : 0);
        const service = Number(order.paid?.service ? (order.paid.service * (total - promoSum - discount)) / 100 : 0);

        rowMap[item.dishId] = {
          id: item.dishId,
          dishName: item.name || prev?.dishName || dishesMap[item.dishId]?.name || 'unknown',
          qnt: item.byWeight ? prev?.qnt || 0 : (prev?.qnt || 0) + 1,
          weight: item.byWeight ? (prev?.weight || 0) + (Number(item.weight) || 0) : prev?.weight || 0,
          unit: item.unit || prev?.unit || 'g',
          byCard: (prev?.byCard || 0) + byCard,
          cash: (prev?.cash || 0) + cash,
          total: (prev?.total || 0) + total,
          promoSum: (prev?.promoSum || 0) + promoSum,
          discount: (prev?.discount || 0) + discount,
          service: (prev?.service || 0) + service,
          group: categoriesMap[dishesMap[item.dishId]?.categoryId || '']?.name || 'unknown',
          section: sectionsMap[categoriesMap[dishesMap[item.dishId]?.categoryId || '']?.sectionId || '']?.name,
          cost: (prev?.cost || 0) + (item.cost || 0),
        };
      });
    });

    return Object.values(rowMap).sort((a, b) => (a.dishName > b.dishName ? 1 : -1));
  }, [categoriesMap, dishesMap, filteredOrders, sectionsMap]);

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => r.dishName,
      },
      {
        name: 'quantity',
        title: t.quantity[lang],
        align: 'right',
        render: (r: Row) =>
          `${r.qnt ? r.qnt : ''}${r.qnt && r.weight ? ' + ' : ''}${r.weight ? `${r.weight}${t[r.unit][lang]}` : ''}`,
      },
      {
        name: 'total',
        title: t.cost[lang],
        align: 'right',
        render: (r: Row) => currency(r.total),
      },
      {
        name: 'promo',
        title: t.promotions[lang],
        align: 'right',
        render: (r: Row) => (r.promoSum ? currency(r.promoSum) : null),
      },
      {
        name: 'discount',
        title: t.discount[lang],
        align: 'right',
        render: (r: Row) => (r.discount ? currency(r.discount) : null),
      },
      {
        name: 'service_charge',
        title: t.service_charge[lang],
        align: 'right',
        render: (r: Row) => (r.service ? currency(r.service) : null),
      },
      {
        name: 'cost',
        title: t.estimated_cost_price[lang],
        align: 'right',
        render: (r: Row) =>
          r.cost ? (
            <Box
              sx={{
                color: (t) =>
                  Math.round((r.cost / (r.total - r.promoSum - r.discount + r.service)) * 100) < 50
                    ? t.palette.text.secondary
                    : t.palette.warning.main,
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', lineHeight: '14px' }}>
                {currency(r.cost)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: '14px' }}>
                ({Math.round((r.cost / (r.total - r.promoSum - r.discount + r.service)) * 100)}%)
              </Typography>
            </Box>
          ) : null,
      },
      {
        name: 'grandTotal',
        title: t.total[lang],
        align: 'right',
        render: (r: Row) => currency(r.total - r.promoSum - r.discount + r.service),
      },
    ],
    [lang]
  );

  const filteredRows = useMemo(() => (section ? rows.filter((r) => r.section === section) : rows), [rows, section]);

  const footerRows: Row[] = useMemo(() => {
    let total = 0;
    let promoSum = 0;
    let discount = 0;
    let service = 0;
    let discountByCard = 0;
    let serviceByCard = 0;
    let promoSumByCard = 0;
    let byCard = 0;
    let cash = 0;
    let cost = 0;

    filteredOrders.forEach((order) => {
      Object.values(order.items).forEach((item) => {
        const itemSectionName =
          sectionsMap[categoriesMap[dishesMap[item.dishId]?.categoryId || '']?.sectionId || '']?.name;

        if (!section || section === itemSectionName) {
          const itemTotal = getOrderItemTotalPrice(item);

          const promoApplied = order.paid?.promotionsApplied?.find((p) => p.appliedForOrderItemIds.includes(item.id));

          const itemPromoSum = promoApplied
            ? promoApplied.discountType === 'percentage'
              ? (itemTotal * Number(promoApplied.discountValue)) / 100
              : promoApplied.discountSum / promoApplied.appliedForOrderItemIds.length
            : 0;

          const itemDiscount = Number(
            order.paid?.discount ? (order.paid.discount * (itemTotal - itemPromoSum)) / 100 : 0
          );

          const itemService = Number(
            order.paid?.service ? (order.paid.service * (itemTotal - itemPromoSum - itemDiscount)) / 100 : 0
          );

          const itemByCard = (itemTotal * getByCard(order.paid)) / (order.paid?.total || 0);

          const itemCash = itemTotal - itemByCard;

          const proportionByCard = itemByCard / itemTotal;

          if (proportionByCard) {
            promoSumByCard += itemPromoSum * proportionByCard;
            discountByCard += itemDiscount * proportionByCard;
            serviceByCard += itemService * proportionByCard;
          }

          total += itemTotal;
          promoSum += itemPromoSum;
          discount += itemDiscount;
          service += itemService;
          byCard += itemByCard;
          cash += itemCash;
          cost += item.cost || 0;
        }
      });
    });

    return [
      {
        id: 'totalByCard',
        dishName: t.by_card[lang],
        qnt: 0,
        weight: 0,
        unit: 'g',
        byCard: 0,
        cash: 0,
        total: byCard,
        promoSum: promoSumByCard,
        discount: discountByCard,
        service: serviceByCard,
        cost: 0,
      },
      {
        id: 'totalCash',
        dishName: t.in_cash[lang],
        qnt: 0,
        weight: 0,
        unit: 'g',
        byCard: 0,
        cash: 0,
        total: cash,
        promoSum: promoSum - promoSumByCard,
        discount: discount - discountByCard,
        service: service - serviceByCard,
        cost: 0,
      },
      {
        id: 'total',
        dishName: t.total[lang],
        qnt: 0,
        weight: 0,
        unit: 'g',
        byCard: 0,
        cash: 0,
        total,
        promoSum,
        discount,
        service,
        cost,
      },
    ];
  }, [categoriesMap, dishesMap, filteredOrders, lang, section, sectionsMap]);

  if (isLoading) {
    return (
      <Box sx={{ mt: 10 }}>
        <Loader open />
      </Box>
    );
  }

  return (
    <>
      <DataTable
        customHeader={
          <>
            <FilterDropdown label={t.menu_section[lang]} value={section} setValue={setSection} options={sections} />
            <FilterDropdown label={t.waiter[lang]} value={workerName} setValue={setWorkerName} options={workerNames} />
            {cafe?.type !== 'selfOrderPoins' && (
              <FilterDropdown label={t.table[lang]} value={tableName} setValue={setTableName} options={tableNames} />
            )}
            <FilterDropdown label={t.order_type[lang]} value={orderType} setValue={setOrderType} options={orderTypes} />
            {cafe?.type === 'selfOrderPoins' && (
              <FilterDropdown
                label={t.self_order_point[lang]}
                value={selfOrderPointId}
                setValue={setSelfOrderPointId}
                options={selfOrderPointIds}
                renderOption={(id) => selfOrderPointsMap[id]?.name || id}
              />
            )}

            {legalEntities.length > 1 && (
              <FilterDropdown
                label={t.legal_entity[lang]}
                value={legalEntityId}
                setValue={setLegalEntityId}
                options={legalEntitiesId}
                renderOption={(id) => legalEntitiesMap[id]?.name || id}
              />
            )}
          </>
        }
        actionButton={
          <div>
            <IconButton onClick={() => setPrint(true)}>
              <Print />
            </IconButton>
          </div>
        }
        rows={filteredRows}
        searchBy="dishName"
        columns={columns}
        size="small"
        footerRows={footerRows}
        showActionButtonOnEmpty={false}
      />

      {timeRange && print && (
        <ReportBySalesPrintModal
          onClose={() => setPrint(false)}
          reportItems={filteredRows}
          totals={footerRows}
          section={section}
          workerName={workerName}
          tableName={tableName}
          orderType={orderType}
          timeRange={timeRange}
        />
      )}
    </>
  );
};

export default ReportBySales;
